import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useParams } from 'react-router-dom';
import { fetchReferralLanding, getReferralLandingErrorMessage } from '../utils/referralLandingApi';
import { portalLoginAuthorizationParams } from '../utils/authSession';
import './ReferralLandingPage.css';

// Presentation is a port of the approved referral landing design
// (keyflo-agent-qr review-site/lp-preview), operator-directed deltas applied:
// "SPECIAL KEYFLO OFFER" eyebrow, no unsubscribe claim, no preview banner.
const ReferralLandingPage = () => {
  const { token } = useParams();
  const location = useLocation();
  const { loginWithRedirect } = useAuth0();
  const [landing, setLanding] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchReferralLanding(token);
        if (!cancelled) setLanding(data);
      } catch (err) {
        if (!cancelled) setError(getReferralLandingErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const shell = (content) => (
    <div className="referral-landing-page">
      <div className="rl-logo-bar">
        <a href="https://keyflo.ai/" aria-label="KeyFlo">
          <img src={`${process.env.PUBLIC_URL || ''}/keyflow_logo.png`} alt="KeyFlo" />
        </a>
      </div>
      <section className="rl-hero">
        <div className="rl-container">{content}</div>
      </section>
      <p className="rl-trust-strip">Built for agents, brokers, and transaction coordinators.</p>
    </div>
  );

  if (loading) {
    return shell(<p className="rl-status">Loading your referral offer…</p>);
  }

  if (error || !landing) {
    return shell(
      <>
        <p className="rl-eyebrow">Special KeyFlo Offer</p>
        <h1 className="rl-headline">Link unavailable</h1>
        <p className="rl-hero-sub">{error || 'This link is invalid or has expired.'}</p>
        <p className="rl-form-note">
          Email <a href="mailto:tc@keyflo.ai">tc@keyflo.ai</a> for help.
        </p>
      </>
    );
  }

  const offer = landing.offered_free_transactions;
  const offerNoun = `free transaction${offer === 1 ? '' : 's'}`;
  const offerValue = landing.offer_value_usd;
  const fullName = (landing.referrer_display_name || 'a KeyFlo user').trim();
  // "a KeyFlo user" is the backend's no-name fallback — never truncate it to "a".
  const referrerFirstName = fullName === 'a KeyFlo user' ? fullName : fullName.split(/\s+/)[0];
  const handleSignUp = () => {
    const returnParams = new URLSearchParams({ referral_token: token });
    const sendId = new URLSearchParams(location.search).get('send');
    if (sendId) {
      returnParams.set('referral_send_id', sendId);
    }
    loginWithRedirect({
      authorizationParams: portalLoginAuthorizationParams({
        screen_hint: 'signup',
      }),
      appState: {
        returnTo: `/profile?${returnParams.toString()}`,
        referralToken: token,
        referralSendId: sendId,
      },
    });
  };

  return shell(
    <>
      <p className="rl-social-proof">
        <span className="rl-avatar-dot" />
        Shared by {fullName}
      </p>
      <p className="rl-eyebrow">Special KeyFlo Offer</p>
      <h1 className="rl-headline">
        {referrerFirstName} sent you {offer} {offerNoun}.
      </h1>
      <p className="rl-hero-sub">
        KeyFlo is the AI transaction coordinator for real estate — contracts, deadlines, and
        compliance handled automatically. Sign up to claim the offer:{' '}
        <strong>
          {offer} {offerNoun}
          {offerValue ? `, a ${offerValue} value` : ''} — limited time only.
        </strong>
      </p>
      <div className="rl-form-wrapper">
        <button type="button" className="rl-btn-primary" onClick={handleSignUp}>
          Claim your {offer} {offerNoun} →
        </button>
        <p className="rl-form-note">
          Next: we&apos;ll email your setup-call booking link — takes two minutes.
        </p>
      </div>
    </>
  );
};

export default ReferralLandingPage;
